import React, {useCallback, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {NoStyleButton} from '@vio/atlas/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook -- This component always displays the Vio logo, not a dynamic brand
import {brands} from '@vio/core/src/brand/config'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

interface InteractiveWrapperProps {
  children: React.ReactNode
  className?: string
}

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** If false, renders only the icon without interactive wrapper. Defaults to true for backward compatibility */
  interactive?: boolean
  /** Custom content to wrap with interactive behavior. If provided, renders this instead of just the icon */
  children?: React.ReactNode
}

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${cssTheme.layout.spacing.s100};
`

const ClickableWrapper = styled(NoStyleButton)`
  display: block;
`

/**
 * Wrapper component that adds interactive behavior (tooltip on desktop, clickable on mobile)
 * to any content. Used internally by InfoIcon and can wrap custom content.
 */
const InteractiveWrapper = ({children, className}: InteractiveWrapperProps) => {
  const {formatMessage} = useIntl()
  const {isDesktop} = useDeviceLayout()
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const {
    commerce: {coBranded}
  } = useBrandCapabilities()

  const {poweredByTooltipTitle, poweredByTooltipText} =
    (coBranded.enabled && coBranded.config.messages) || {}

  const defaultTooltipContent = poweredByTooltipText ? (
    <FormattedMessageWrapper {...poweredByTooltipText} />
  ) : null

  const finalTitle =
    poweredByTooltipTitle && poweredByTooltipTitle.id
      ? String(formatMessage(poweredByTooltipTitle))
      : ''

  const finalContent = defaultTooltipContent

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsBottomSheetOpen(true)
  }, [])

  const handleCloseBottomSheet = useCallback(() => {
    setIsBottomSheetOpen(false)
  }, [])

  // Hide interactive behavior if no brand messages are configured
  const shouldShowInteractive = Boolean(finalContent || finalTitle)

  if (!shouldShowInteractive) {
    return <>{children}</>
  }

  return (
    <>
      {isDesktop ? (
        <Tooltip
          placement={TooltipPlacement.BottomStart}
          title={finalTitle}
          content={finalContent}
        >
          {children}
        </Tooltip>
      ) : (
        <ClickableWrapper
          onClick={handleClick}
          type="button"
          className={className}
        >
          {children}
        </ClickableWrapper>
      )}
      {!isDesktop && (
        <BottomSheet
          isOpen={isBottomSheetOpen}
          onClose={handleCloseBottomSheet}
          navBarProps={{
            centerContent: (
              <>
                <FormattedMessageWrapper
                  id="partners.poweredBy"
                  defaultMessage="Powered by"
                />{' '}
                {brands.vio.name}
              </>
            )
          }}
        >
          {finalTitle && <Text variant="titleL">{finalTitle}</Text>}
          {finalContent && <Text variant="bodyM">{finalContent}</Text>}
        </BottomSheet>
      )}
    </>
  )
}

/**
 * Component that provides interactive behavior (tooltip/BottomSheet) for PoweredBy content.
 * Can render an info icon or wrap custom content with interactive behavior.
 */
export const PoweredByInteractive = ({
  className,
  interactive = true,
  children
}: Props) => {
  const {formatMessage} = useIntl()
  const {
    commerce: {coBranded}
  } = useBrandCapabilities()
  const {poweredByTooltipTitle, poweredByTooltipText} =
    (coBranded.enabled && coBranded.config.messages) || {}

  const defaultTooltipContent = poweredByTooltipText ? (
    <FormattedMessageWrapper {...poweredByTooltipText} />
  ) : null

  const finalTitle =
    poweredByTooltipTitle && poweredByTooltipTitle.id
      ? String(formatMessage(poweredByTooltipTitle))
      : ''

  const finalContent = defaultTooltipContent

  // Hide info icon if no brand messages are configured
  const shouldShowInfoIcon = Boolean(finalContent || finalTitle)

  // If custom children provided, always wrap them with interactive behavior
  // (InteractiveWrapper will handle the case where there's no content)
  if (children) {
    return (
      <InteractiveWrapper className={className}>{children}</InteractiveWrapper>
    )
  }

  // For icon-only usage, check if we should show it
  if (!shouldShowInfoIcon) {
    return null
  }

  const iconElement = (
    <InfoIconWrapper className={className}>
      <Icon
        name="Information"
        size="sm"
        colorPath="content.neutral.secondary"
      />
    </InfoIconWrapper>
  )

  // If not interactive, just return the icon
  if (!interactive) {
    return iconElement
  }

  // Default: wrap icon with interactive behavior
  return (
    <InteractiveWrapper className={className}>{iconElement}</InteractiveWrapper>
  )
}
